import {Injectable} from '@angular/core';
import {
    DeleteFileOptions,
    Directory,
    Encoding,
    Filesystem,
    GetUriOptions,
    MkdirOptions,
    ReaddirOptions,
    ReadFileOptions,
    RmdirOptions,
    StatOptions,
    WriteFileOptions
} from '@capacitor/filesystem';
import {CopyOptions} from '@capacitor/filesystem/dist/esm/definitions';
import {AlertController} from '@ionic/angular';
import {ALERT_TXT} from '../../strings/language';
import {Capacitor} from '@capacitor/core';
import {AndroidSettings, IOSSettings, NativeSettings} from 'capacitor-native-settings';
import {FilePath} from '@awesome-cordova-plugins/file-path/ngx';
import {APP_ASSIGN_FOLDER, APP_ASSIGN_FOLDER_ANDROID} from '../../../environments/environment';
import {Router} from '@angular/router';
import {MessagesService} from '../messages/messages.service';
import {CMFilemanager} from 'capacitor-plugin-cmfilemanager';


const APP_DIRECTORY = Directory.Documents;
const APP_FILES_DIRECTORY = Directory.Data;

@Injectable({
    providedIn: 'root'
})
export class DateiSystemService {

    alert: HTMLIonAlertElement;

    constructor(private alertCtrl: AlertController,
                private filePath: FilePath,
                private router: Router,
                private msgService: MessagesService) {
    }

    async checkFilesystemPermissions(): Promise<any> {
        return Filesystem.checkPermissions()
            .then((result) => {
                console.log('### checkFilesystemPermissions ###: ', result);
                if (result.publicStorage === 'granted') {
                    return true;
                } else if (result.publicStorage === 'denied') {
                    this.openAppSettings();
                    return false;
                }
            })
            .catch(() => false);
    }

    async alertPermissions() {
        this.alert = await this.alertCtrl.create({
            header: ALERT_TXT.header.str00010,
            subHeader: 'regisafe hat nicht die notwendigen Berechtigungen um auf den Speicher zuzugreifen.',
            buttons: ['Ok']
        });

        this.alert.present();
    }

    /**
     * pruefeAppOrder()
     *
     * Prüft ob ein Ordner existiert und gibt das Ergebnis
     * als Boolschen Wert zurück
     *
     * @param ordnername
     */
    async pruefeAppOrder(ordnername: string): Promise<any> {
        const options: ReaddirOptions = {
            path: ordnername,
            directory: APP_DIRECTORY
        };

        return Filesystem.readdir(options)
            .then((data) => {
                // console.log('Filesystem readdir: ', data, APP_DIRECTORY);
                return true;
            })
            .catch(() => false);
    }

    /**
     * erstelleAppOrdner()
     *
     * Erstellt einen Ordner im angegebenen Verzeichniss und
     * gibt das Ergebnis als Boolschen Wert zurück
     *
     * @param ordnername
     */
    async erstelleAppOrdner(ordnername: string): Promise<any> {
        const option: MkdirOptions = {
            path: ordnername,
            directory: APP_DIRECTORY,
            recursive: false
        };
        return Filesystem.mkdir(option)
            .then(() => true)
            .catch(() => false);
    }

    async schreibeDatei(ordnername: string, dateiname: string, content: string): Promise<any> {
        const options: WriteFileOptions = {
            directory: APP_DIRECTORY,
            encoding: Encoding.UTF8,
            data: content,
            path: ordnername + '/' + dateiname
        };

        return Filesystem.writeFile(options)
            .then((data) => {
                return data;
            })
            .catch((error) => {
                return error;
            });
    }

    async leseDatei(pfad: string, dateiname: string): Promise<any> {
        const options: ReadFileOptions = {
            directory: APP_DIRECTORY,
            path: pfad + '/' + dateiname,
            encoding: Encoding.UTF8
        };

        return Filesystem.readFile(options)
            .then((data) => {
                // console.log('leseDatei: ', data);
                return data;
            })
            .catch((error) => {
                // console.log('leseDatei error: ', error);
                return false;
            });
    }

    async loescheOrdner(pfad: string): Promise<any> {
        const option: RmdirOptions = {
            path: pfad,
            directory: APP_DIRECTORY,
            recursive: true
        };

        return Filesystem.rmdir(option)
            .then((data) => {
                return true;
            })
            .catch((error) => {
                return false;
            });
    }

    async loescheDatei(pfad: string): Promise<any> {
        const option: DeleteFileOptions = {
            path: pfad,
            directory: APP_DIRECTORY
        };

        return Filesystem.deleteFile(option)
            .then((data) => {
                return true;
            })
            .catch((error) => {
                return false;
            });
    }

    async loescheDateiAndroid(pfad: string): Promise<any> {
        const option: DeleteFileOptions = {
            path: pfad,
            directory: APP_FILES_DIRECTORY
        };

        return Filesystem.deleteFile(option)
            .then((data) => {
                return true;
            })
            .catch((error) => {
                return false;
            });
    }
    // https://file-examples.com/
    // https://www.youtube.com/watch?v=KdIgThOqqhU

    async downloadDatei() {
        // return Filesystem.
    }

    async schreibeKonfiguration(ordnername: string, dateiname: string, content: string): Promise<any> {
        const options: WriteFileOptions = {
            directory: APP_DIRECTORY,
            encoding: Encoding.UTF8,
            data: content,
            path: ordnername + '/' + dateiname
        };

        return Filesystem.writeFile(options)
            .then((data) => {
                console.log('writeFile: ', data);
                return true;
            })
            .catch((error) => {
                console.log('writeFile error: ', error);
                return false;
            });
    }

    async getURI(filePath): Promise<any> {
        const options: GetUriOptions = {
            directory: APP_DIRECTORY,
            path: filePath
        }

        return Filesystem.getUri(options)
            .then((data) => {
                console.log('getURI: ', data);
                return data;
            })
            .catch((error) => {
                console.log('getURI: ', error);
                return error;
            });
    }

    async getFileInfo(filePath) {
        const options: StatOptions = {
            path: filePath,
            directory: APP_DIRECTORY
        };
        return Filesystem.stat(options);
    }

    async getFileInfoAndroid(filePath) {
        const options: StatOptions = {
            path: filePath,
            directory: APP_FILES_DIRECTORY
        };
        return Filesystem.stat(options);
    }

    async getFileUri(filePath) {
        const options: StatOptions = {
            path: filePath,
            directory: APP_DIRECTORY
        };
        return Filesystem.stat(options);
    }


    async kopiereDateiNach(fromPath, targetFolder, filename) {
        const options: CopyOptions = {
            from: fromPath,
            to: targetFolder + filename,
            toDirectory: APP_DIRECTORY
        };

        return Filesystem.copy(options)
            .then((data) => {
                console.log('kopiereDateiNach: ', data);
                return true;
            })
            .catch((error) => {
                console.log('kopiereDateiNach error: ', error);
                return false;
            });
    }

    async leseDateienInOrdner(ordnername: string): Promise<any> {
        const options: ReaddirOptions = {
            path: ordnername,
            directory: APP_DIRECTORY
        };

        return Filesystem.readdir(options)
            .then((data) => data)
            .catch((e) => e);
    }

    async leseDateienInOrdnerAndroid(ordnername: string): Promise<any> {
        const options: ReaddirOptions = {
            path: ordnername,
            directory: APP_FILES_DIRECTORY
        };

        return Filesystem.readdir(options)
            .then((data) => data)
            .catch((e) => e);
    }

    async readIQOFile(url) {
        return Filesystem.readFile({path: url});
    }

    async schreibeDateiHash(content: string): Promise<any> {

        const options: WriteFileOptions = {
            directory: Directory.External,
            encoding: Encoding.UTF8,
            data: content,
            path: 'regisafe.hash.txt'
        };

        return Filesystem.writeFile(options)
            .then((data) => {
                return true;
            })
            .catch((error) => {
                return false;
            });
    }

    async leseDateiHash(): Promise<any> {
        const options: ReadFileOptions = {
            directory: Directory.External,
            path: 'regisafe.hash.txt',
            encoding: Encoding.UTF8
        };

        return Filesystem.readFile(options)
            .then((data) => {
                return data;
            })
            .catch((error) => {
                return false;
            });
    }

    /**
     * openAppSettings()
     *
     * öffnet auf dem Gerät die Einstellungen zur App
     *
     */
    async openAppSettings() {
        const alert = await this.alertCtrl.create({
            header: ALERT_TXT.header.str00030,
            message: ALERT_TXT.message.str00080,
            buttons: [{
                text: ALERT_TXT.buttons.str00010,
                role: 'cancel',
                handler: () => {

                }
            }, {
                text: ALERT_TXT.buttons.str00280,
                role: 'add',
                handler: async () => {
                    if (Capacitor.getPlatform() === 'ios') {
                        await NativeSettings.openIOS({
                            option: IOSSettings.App,
                        });
                    } else {
                        await NativeSettings.openAndroid({
                            option: AndroidSettings.ApplicationDetails,
                        });
                    }
                }
            }]
        });
        await alert.present();
    }


    copyIntentFiles(clipItems: Array<any>) {

        this.filePath.resolveNativePath(clipItems[0].uri)
            .catch((e) => {
                console.log('resolveNativePath error: ', e);
                return false;
            })
            .then(async (uri: string) => {
                const dateiname = uri.split('/').pop();
                const kopiere = await this.kopiereDateiNach(uri, APP_ASSIGN_FOLDER, dateiname);

                clipItems.splice(0, 1);
                if (clipItems.length > 0) {
                    setTimeout(() => {
                        this.copyIntentFiles(clipItems);
                    }, 500);

                } else {
                    if (this.router.url === '/regisafe/zuordnen') {
                        this.msgService.sendMessage({context: 'intent:copied'});
                    } else {
                        this.router.navigate(['/regisafe/zuordnen']);
                    }
                }

            });
    }

    async copySharedFile(filePath: string) {
        const dateiname = decodeURI(filePath.split('/').pop());
        const kopiere = await this.kopiereDateiNach(filePath, APP_ASSIGN_FOLDER, dateiname);
        if (this.router.url === '/regisafe/zuordnen') {
            this.msgService.sendMessage({context: 'intent:copied'});
        } else {
            await this.router.navigate(['/regisafe/zuordnen']);
        }
    }

    /**
     * CMFilemanager_openFile()
     *
     * open a File for editing under iOS higher 17
     *
     * @param file
     * @constructor
     */
    async CMFilemanager_openFile(file): Promise<any> {
        const localpath: string = file.path + '/' + file.name;
        return CMFilemanager.openFile({pathURL: localpath})
    }

}
